import React from 'react';

const cn = (...classes: (string | boolean | undefined | null)[]): string =>
  classes.filter(Boolean).join(' ');

export type ButtonVariant = 'primary' | 'primary-outline' | 'secondary' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  'primary-outline': 'btn-primary-outline',
  secondary:
    'border border-stone-200 text-stone-700 bg-white hover:bg-stone-50 focus:ring-stone-400',
  danger:
    'border border-red-200 text-red-700 bg-white hover:bg-red-50 focus:ring-red-400',
  ghost: 'text-stone-600 hover:bg-stone-100 hover:text-stone-900',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  children: React.ReactNode;
  /** Render as link (uses same visual style). */
  as?: 'button' | 'a';
  href?: string;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  as = 'button',
  href,
  disabled,
  ...rest
}) => {
  const base =
    'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  const combined = cn(
    base,
    variantClasses[variant],
    sizeClasses[size],
    className
  );

  if (as === 'a' && href) {
    return (
      <a href={href} className={combined} {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}>
        {children}
      </a>
    );
  }

  return (
    <button type="button" className={combined} disabled={disabled} {...rest}>
      {children}
    </button>
  );
};

export default Button;
